import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Database,
  Calculator,
  Scale,
  Gavel,
  ShieldCheck,
  FlaskConical,
} from 'lucide-react'
import { reconciliationService } from '@/services/reconciliation'
import type { TraceCheck } from '@/types'

/* ── formatting ────────────────────────────────────────────────────────── */

function fmtValue(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  if (Number.isInteger(v) && Math.abs(v) >= 1000) return v.toLocaleString()
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 4 })
  // Ratios and rates: full precision is the point — this is the 1e-9 story.
  return String(v)
}

function outcomeBadge(outcome: TraceCheck['outcome'] | string) {
  const map: Record<string, string> = {
    PASS: 'text-emerald-400 border-emerald-400/30 bg-emerald-400/[0.06]',
    EXCEPTION: 'text-amber-400 border-amber-400/30 bg-amber-400/[0.06]',
    REFER: 'text-sky-400 border-sky-400/30 bg-sky-400/[0.06]',
    MISSING_INPUT: 'text-red-400 border-red-400/30 bg-red-400/[0.06]',
    INCOMPLETE: 'text-red-400 border-red-400/30 bg-red-400/[0.06]',
  }
  return map[outcome] ?? 'text-stone-400 border-stone-500/30 bg-stone-500/[0.06]'
}

/* ── stage chrome ──────────────────────────────────────────────────────── */

function Stage({
  n,
  title,
  icon: Icon,
  note,
  children,
}: {
  n: string
  title: string
  icon: typeof FileSpreadsheet
  note?: string
  children: React.ReactNode
}) {
  return (
    <div className="card p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-400/10 border border-amber-400/20 flex-shrink-0">
          <Icon size={15} className="text-amber-300" />
        </div>
        <div className="min-w-0">
          <div className="terminal-kicker">
            {n} · {title}
          </div>
          {note && <p className="text-[11px] leading-relaxed text-stone-500 mt-1 max-w-3xl">{note}</p>}
        </div>
      </div>
      {children}
    </div>
  )
}

const th = 'text-left py-2 px-3 text-[9px] font-mono uppercase tracking-[0.16em] text-stone-500'
const td = 'py-1.5 px-3 align-top'

/* ── page ──────────────────────────────────────────────────────────────── */

const WITHHOLDABLE: { field: string; label: string }[] = [
  { field: 'stabilized_noi_or_ncf', label: 'Withhold NOI / NCF' },
  { field: 'stabilized_value', label: 'Withhold stabilized value' },
]

export default function GoldenDealTrace() {
  const { model = '' } = useParams()
  const [withheld, setWithheld] = useState<string | null>(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['golden-deal-trace', model],
    queryFn: () => reconciliationService.getTrace(model),
    enabled: Boolean(model),
    staleTime: 60_000,
  })
  const whatIf = useQuery({
    queryKey: ['golden-deal-what-if', model, withheld],
    queryFn: () => reconciliationService.getWhatIfMissing(model, withheld as string),
    enabled: Boolean(model && withheld),
    staleTime: 60_000,
  })

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/golden-deal-reconciliation"
            className="flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-300 transition-colors"
          >
            <ArrowLeft size={13} /> All deals
          </Link>
          <div className="w-px h-4 bg-amber-400/20" />
          <h1 className="text-lg font-semibold text-stone-100">{data?.display ?? model}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/credit-box" className="text-[11px] text-amber-300 hover:text-amber-200 font-mono">
            Credit Box v{data?.rules_version ?? '2.0'} →
          </Link>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 font-mono">
            <ShieldCheck size={13} /> No-AI zone · deterministic
          </span>
        </div>
      </div>

      {isLoading && <div className="card p-6 text-[12px] text-stone-500 font-mono">Running the trace…</div>}
      {isError && <div className="card p-6 text-[12px] text-red-400 font-mono">Failed to load the run trace.</div>}

      {data && (
        <>
          {/* 01 · SOURCE */}
          <Stage
            n="01"
            title="Source Workbook — Deterministic Extraction"
            icon={FileSpreadsheet}
            note={data.stages.source.doctrine}
          >
            <div className="text-[12px] font-mono text-stone-300 mb-2">
              {data.stages.source.workbook}
              <span className="text-stone-600"> · {data.stages.source.anchors.length} anchored cells</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-amber-400/10">
                    <th className={th}>Metric</th>
                    <th className={th}>Label Cell</th>
                    <th className={th}>Verified Label</th>
                    <th className={th}>Value Cell</th>
                    <th className={th}>Extracted Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stages.source.anchors.map((a) => (
                    <tr key={a.metric} className="border-b border-amber-400/[0.05]">
                      <td className={`${td} font-mono text-stone-300`}>{a.metric}</td>
                      <td className={`${td} font-mono text-stone-500`}>
                        {a.sheet}!{a.label_cell}
                      </td>
                      <td className={`${td} text-stone-400`}>
                        <span className="inline-flex items-center gap-1">
                          <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
                          “{a.expect_label}”
                        </span>
                      </td>
                      <td className={`${td} font-mono text-stone-500`}>
                        {a.sheet}!{a.value_cell}
                      </td>
                      <td className={`${td} font-mono text-stone-200`}>{fmtValue(a.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Stage>

          {/* 02 · RECORD */}
          <Stage n="02" title="Structured Record" icon={Database} note={data.stages.record.note}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
              {Object.entries(data.stages.record.values).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3 border-b border-amber-400/[0.05] py-1">
                  <span className="text-[11px] font-mono text-stone-500 truncate">{k}</span>
                  <span className="text-[12px] font-mono text-stone-200 whitespace-nowrap">{fmtValue(v)}</span>
                </div>
              ))}
            </div>
          </Stage>

          {/* 03 · RECOMPUTATION */}
          <Stage
            n="03"
            title="Independent Recomputation"
            icon={Calculator}
            note={data.stages.computed.note}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-amber-400/10">
                    <th className={th}>Metric</th>
                    <th className={th}>Formula</th>
                    <th className={th}>Engine Result</th>
                    <th className={th}>Workbook Cached</th>
                    <th className={th}>Agreement</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stages.computed.steps.map((s) => (
                    <tr key={s.metric} className="border-b border-amber-400/[0.05]">
                      <td className={`${td} font-mono text-stone-300`}>{s.metric}</td>
                      <td className={`${td} text-stone-400`}>
                        {s.formula}
                        <div className="text-[10px] font-mono text-stone-600 mt-0.5">
                          {Object.entries(s.inputs)
                            .map(([k, v]) => `${k} = ${fmtValue(v)}`)
                            .join(' · ')}
                        </div>
                      </td>
                      <td className={`${td} font-mono text-stone-200 whitespace-nowrap`}>{fmtValue(s.engine_value)}</td>
                      <td className={`${td} font-mono text-stone-400 whitespace-nowrap`}>{fmtValue(s.workbook_value)}</td>
                      <td className={td}>
                        {s.exact ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-mono">
                            <CheckCircle2 size={12} /> Exact
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-red-400 font-mono">
                            <AlertTriangle size={12} /> Mismatch
                            {s.delta !== undefined && (
                              <span className="text-red-400/70">Δ {s.delta.toExponential(3)}</span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] font-mono text-stone-500 mt-2">
              Tolerance: {data.stages.computed.tolerance} ·{' '}
              {data.stages.computed.all_exact ? (
                <span className="text-emerald-400">all recomputations exact</span>
              ) : (
                <span className="text-red-400">mismatch present — investigate before relying on this run</span>
              )}
            </div>
          </Stage>

          {/* 04 · SCREEN */}
          <Stage
            n="04"
            title={`Credit-Box Screen — ${data.stages.screen.program_name}`}
            icon={Scale}
            note={`Every test below is a line in Credit Box v${data.stages.screen.rules_version} (artifact ${data.stages.screen.rules_sha256.slice(0, 12)}… — stamped on this result). The deal's actual figure is shown against the rule; judgment factors are never decided here — they route to human review by design.`}
          >
            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500 mb-1.5">
                Inputs to the screen — with provenance
              </div>
              <div className="flex flex-wrap gap-2">
                {data.stages.screen.inputs.map((i) => (
                  <span
                    key={i.field}
                    className="inline-flex items-baseline gap-1.5 text-[11px] font-mono px-2 py-1 rounded border border-amber-400/15 bg-amber-400/[0.04]"
                  >
                    <span className="text-stone-400">{i.field}</span>
                    <span className="text-stone-200">{fmtValue(i.value)}</span>
                    {i.source_cell && <span className="text-stone-600">← {i.source_cell}</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              {data.stages.screen.checks.map((c) => (
                <div
                  key={c.name}
                  className="flex items-start gap-3 border-b border-amber-400/[0.05] py-1.5"
                >
                  <span
                    className={`inline-block w-28 flex-shrink-0 text-center text-[10px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded border ${outcomeBadge(c.outcome)}`}
                  >
                    {c.outcome.replace('_', ' ')}
                  </span>
                  <div className="min-w-0">
                    <span className="text-[12px] font-mono text-stone-200">{c.name}</span>
                    <span className="text-[12px] text-stone-500"> — {c.detail}</span>
                    {c.fill_hint && (
                      <div className="text-[11px] text-red-400/90 mt-0.5">↳ {c.fill_hint}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {data.stages.screen.attachments.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500 mb-1.5">
                  Capital stack — cumulative attachment (senior-most first)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="border-b border-amber-400/10">
                        <th className={th}>Tranche</th>
                        <th className={th}>Cumulative Amount</th>
                        <th className={th}>Cumulative LTV</th>
                        <th className={th}>Cumulative Debt Yield</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.stages.screen.attachments.map((a) => (
                        <tr key={a.name} className="border-b border-amber-400/[0.05]">
                          <td className={`${td} font-mono text-stone-300`}>{a.name}</td>
                          <td className={`${td} font-mono text-stone-200`}>{fmtValue(a.cumulative_amount)}</td>
                          <td className={`${td} font-mono text-stone-400`}>{fmtValue(a.cumulative_ltv)}</td>
                          <td className={`${td} font-mono text-stone-400`}>{fmtValue(a.cumulative_debt_yield)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Stage>

          {/* 05 · VERDICT */}
          <Stage
            n="05"
            title="Verdict & Reconciliation"
            icon={Gavel}
            note="These five deals are the calibration set — the deals Credit Box v2.0 was codified against; correspondence is reconciliation against your recorded decisions, not an out-of-sample prediction. The screen never renders a decision: PASS means in envelope with judgment referred; EXCEPTION names the exact line a human must widen or kill; INCOMPLETE means a required input is absent and nothing is assumed."
          >
            <div className="flex items-center gap-4 mb-3">
              <span
                className={`inline-block text-[12px] font-mono uppercase tracking-[0.12em] px-3 py-1 rounded border ${outcomeBadge(data.stages.verdict.outcome)}`}
              >
                {data.stages.verdict.outcome}
              </span>
              {data.stages.verdict.exceptions.length > 0 && (
                <span className="text-[12px] font-mono text-amber-400">
                  exception: {data.stages.verdict.exceptions.join(', ')}
                </span>
              )}
              <span className="text-[12px] font-mono text-stone-500">
                {data.stages.verdict.referrals.length} judgment factors referred to human review
              </span>
            </div>

            {data.stages.verdict.proceeds_shortfall && (
              <div className="grid grid-cols-3 gap-3 mb-3 max-w-xl">
                <div className="metric-cell">
                  <div className="metric-cell-label">Ask</div>
                  <div className="metric-cell-value text-stone-100">
                    ${(data.stages.verdict.proceeds_shortfall.ask / 1e6).toFixed(1)}M
                  </div>
                </div>
                <div className="metric-cell">
                  <div className="metric-cell-label">Supportable</div>
                  <div className="metric-cell-value text-stone-100">
                    ${(data.stages.verdict.proceeds_shortfall.supportable / 1e6).toFixed(1)}M
                  </div>
                </div>
                <div className="metric-cell">
                  <div className="metric-cell-label">Shortfall</div>
                  <div className="metric-cell-value text-amber-400">
                    ${(data.stages.verdict.proceeds_shortfall.gap / 1e6).toFixed(1)}M
                    <span className="text-[11px] text-stone-500 ml-1">
                      ({Math.round(data.stages.verdict.proceeds_shortfall.supportable_pct_of_ask * 100)}% of ask)
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="border-l-2 border-amber-400/20 pl-3">
              <div className="text-[12px] text-stone-300 mb-1">
                Your decision: <span className="font-medium text-stone-100">{data.stages.verdict.reconciliation.actual}</span>
                <span className="text-stone-500">
                  {' '}
                  — {data.stages.verdict.reconciliation.actual_reasons.join('; ')}
                </span>
              </div>
              <div className="text-[12px] leading-relaxed text-stone-400">
                {data.stages.verdict.reconciliation.note}
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.1em] text-emerald-400">
                <CheckCircle2 size={12} /> {data.stages.verdict.reconciliation.verdict}
              </div>
            </div>
          </Stage>

          {/* 06 · RESILIENCE SANDBOX */}
          <Stage
            n="06"
            title="Resilience — What If an Input Were Missing?"
            icon={FlaskConical}
            note="Sandbox: re-screen this deal with a required input deliberately withheld. The committed run above is untouched and nothing is persisted. The point: an absent number can never read as passing — the screen says exactly what is missing and where it lives."
          >
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {WITHHOLDABLE.map((w) => (
                <button
                  key={w.field}
                  onClick={() => setWithheld(withheld === w.field ? null : w.field)}
                  className={`text-[11px] font-mono px-3 py-1.5 rounded border transition-colors ${
                    withheld === w.field
                      ? 'border-amber-400/50 bg-amber-400/10 text-amber-300'
                      : 'border-amber-400/15 bg-transparent text-stone-400 hover:text-stone-200 hover:border-amber-400/30'
                  }`}
                >
                  <FlaskConical size={11} className="inline mr-1.5 -mt-0.5" />
                  {w.label}
                </button>
              ))}
              {withheld && (
                <button
                  onClick={() => setWithheld(null)}
                  className="text-[11px] font-mono text-stone-500 hover:text-stone-300 px-2"
                >
                  reset
                </button>
              )}
            </div>

            {withheld && whatIf.isLoading && (
              <div className="text-[12px] text-stone-500 font-mono">Re-screening without {withheld}…</div>
            )}
            {withheld && whatIf.data && (
              <div className="border border-amber-400/15 rounded-lg p-3 bg-amber-400/[0.02]">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`inline-block text-[11px] font-mono uppercase tracking-[0.12em] px-2.5 py-0.5 rounded border ${outcomeBadge(whatIf.data.verdict.outcome)}`}
                  >
                    {whatIf.data.verdict.outcome}
                  </span>
                  <span className="text-[11px] font-mono text-stone-500">
                    {whatIf.data.withheld_field} withheld
                    {whatIf.data.withheld_source_cell && ` (normally from ${whatIf.data.withheld_source_cell})`}
                    {' '}· sandbox — nothing persisted
                  </span>
                </div>
                <div className="space-y-1.5">
                  {whatIf.data.screen.checks
                    .filter((c) => c.outcome !== 'REFER')
                    .map((c) => (
                      <div key={c.name} className="flex items-start gap-3 py-1 border-b border-amber-400/[0.05]">
                        <span
                          className={`inline-block w-28 flex-shrink-0 text-center text-[10px] font-mono uppercase tracking-[0.1em] px-1.5 py-0.5 rounded border ${outcomeBadge(c.outcome)}`}
                        >
                          {c.outcome.replace('_', ' ')}
                        </span>
                        <div className="min-w-0">
                          <span className="text-[12px] font-mono text-stone-200">{c.name}</span>
                          <span className="text-[12px] text-stone-500"> — {c.detail}</span>
                          {c.fill_hint && (
                            <div className="text-[11px] text-red-400/90 mt-0.5">↳ {c.fill_hint}</div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
                <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">{whatIf.data.note}</p>
              </div>
            )}
          </Stage>
        </>
      )}
    </div>
  )
}
